import {
    Button,
    Grid,
    Step,
    StepConnector,
    StepLabel,
    Stepper,
    Theme,
    useMediaQuery,
    withStyles
} from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Check from '@material-ui/icons/Check';
import {createStyles, makeStyles, useTheme} from '@material-ui/styles';
import React from 'react';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => ({
    stepperContainer: {
        textAlign: 'right',
    },
    stepper: {
        backgroundColor: 'transparent',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
        },

        '& .MuiStep-alternativeLabel': {
            [theme.breakpoints.down('sm')]: {
                flexGrow: 0,
            },
        }
    },
    step: {
        fontSize: 14,
        color: '#696969',
        '& $completed': {
            color: theme.palette.primary.main,
        },
        '& $active': {
            color: theme.palette.primary.main,
        },

        '& .MuiStepLabel-labelContainer': {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            },
        }
    },
    stepText: {
        width: '100%',
        textAlign: 'center',
        marginTop: -theme.spacing(1),
    },
    active: {}, //needed so that the &$active tag works
    completed: {},
    navigationButton: {
        textTransform: 'none',
        width: '100%',
        fontSize: 18,
        color: theme.palette.primary.main,

        '&:hover, &.Mui-focusVisible': {
            textTransform: 'none',
        },

        '&:hover': {
            backgroundColor: 'transparent'
        },

        '&.Mui-disabled': {
            color: theme.palette.black,
            opacity: 0.3
        }
    },
    containerBackButton: {
        [theme.breakpoints.up('md')]: {
            position: 'absolute',
            left: theme.spacing(5),

            '& $navigationButton': {
                marginTop: theme.spacing(2),
                fontSize: 14
            }
        }
    }
}));

const BookingStepConnector = withStyles((theme: Theme) => ({
    alternativeLabel: {
        top: 9,
    },
    active: {
        '& .MuiStepConnector-line': {
            top: 8,
            height: 2,
            marginLeft: -12,
            marginRight: -12,
            backgroundColor: theme.palette.primary.main,
        }
    },
    completed: {
        '& .MuiStepConnector-line': {
            top: 8,
            height: 2,
            marginLeft: -12,
            marginRight: -12,
            backgroundColor: theme.palette.primary.main,
        }
    },
    line: {
        height: 1,
        border: 0,
        backgroundColor: '#b2b2b2',
        borderRadius: 0,
        marginLeft: 0,
        marginRight: 0,
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
            marginLeft: -6,
            marginRight: -6,
        },
    },
}))(StepConnector);

const useBookingStepIconStyles = makeStyles((theme: Theme) => ({
    root: {
        backgroundColor: theme.palette.white,
        zIndex: 1,
        display: 'flex',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
        borderColor: '#696969',
        borderStyle: 'solid',
        borderWidth: 2,
        width: 17,
        height: 17,

        [theme.breakpoints.down('sm')]: {
            borderColor: theme.palette.primary.main,
            width: 9,
            height: 9,
        },
    },
    active: {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
    },
    completed: {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        cursor: 'pointer',
        color: theme.palette.white,
        fontSize: 8,

        [theme.breakpoints.down('sm')]: {
            color: theme.palette.primary.main,
        },
    },
}));

const BookingStepIcon = (props: { active: boolean, completed: boolean, icon: React.ReactNode, }) => {
    const classes = useBookingStepIconStyles();
    const {active, completed} = props;

    return (
        <div className={clsx(classes.root, { [classes.active]: active })}>
            {completed ? <Check className={clsx(classes.root, classes.completed)} /> : null}
        </div>
    );
};

const ProgressIndicator = (props: {
    logo: string,
    activeStep: number,
    steps: string[],
    setActiveStep: (step: number) => void,
    activeStepValid: boolean,
    adminVersion: boolean,
    isExistingBooking: boolean
}) => {
    const classes = useStyles();
    const theme: Theme = useTheme();
    const showNext = useMediaQuery(theme.breakpoints.down('sm'), {
        defaultMatches: true
    });

    let showBackButton = true;
    if(props.activeStep === 0) showBackButton = false;
    if(props.activeStep === 1 && props.isExistingBooking) showBackButton = false;

    const handleBackClick = () => {
        props.setActiveStep(props.activeStep - 1);
        if(props.activeStep === 3 && props.isExistingBooking) props.setActiveStep(1);
        if(props.activeStep === 5 && props.isExistingBooking) props.setActiveStep(3);
    };

    return (
        <Grid container spacing={1}>
            <Grid item xs={3} className={classes.containerBackButton}>
                <Button
                    className={classes.navigationButton}
                    onClick={handleBackClick}
                    disabled={!showBackButton}
                    disableRipple
                >
                    <ArrowBackIosIcon />
                    Back
                </Button>
            </Grid>
            <Grid item md={12} xs={6} className={classes.stepperContainer}>
                <Stepper className={classes.stepper} alternativeLabel activeStep={props.activeStep - 1}
                         connector={<BookingStepConnector/>}>
                    {props.steps.map((label, i) => i > 0 && (
                        <Step key={label}>
                            <StepLabel
                                onClick={i <= props.activeStep ? () => props.setActiveStep(i) : undefined}
                                classes={{
                                    alternativeLabel: classes.step,
                                    completed: classes.completed,
                                    active: classes.active
                                }} StepIconComponent={BookingStepIcon}>{showNext ? null : label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Grid>
            {showNext && (
                <Grid item xs={3}>
                    <Button
                        className={classes.navigationButton}
                        onClick={() => props.setActiveStep(props.activeStep + 1)}
                        disabled={!props.activeStepValid}
                        disableRipple
                    >
                        Next
                        <ArrowForwardIosIcon />
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};

export default ProgressIndicator;
